import { Button } from '@synoptic/ui-kit/button/button.js';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { PasswordInput } from '@synoptic/ui-kit/input/password-input.tsx';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { FC, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { passwordPattern } from '../../form-utils/password-pattern';
import {
  formHeading,
  formFieldsContainer,
  subButton,
  submitAuthFormButton,
  cardBody,
} from '../_auth/page.css';

type FormInputs = {
  confirmationCode: string;
  newPassword: string;
};

export const ConfirmResetPasswordForm: FC<{
  onConfirmResetPassword: () => void;
  username: string;
}> = ({ onConfirmResetPassword, username }) => {
  const { handleSubmit, formState, reset, register } = useForm<FormInputs>();
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const [isResending, setIsResending] = useState(false);

  const handleResendEmail = async () => {
    try {
      setIsResending(true);
      await resetPassword({ username });
    } catch (e) {
      toast.error({ title: String(e) });
    } finally {
      setIsResending(false);
    }
  };

  const submit: SubmitHandler<FormInputs> = async (data) => {
    const { confirmationCode, newPassword } = data;
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });

      onConfirmResetPassword();
      reset();
    } catch (e) {
      toast.error({ title: String(e) });
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(submit)}>
      <h1 className={formHeading}>Update your password</h1>
      <p className={cardBody}>
        We’ve just sent a 6-digit code to you email. Please check your inbox and
        enter the code below:
      </p>
      <div className={formFieldsContainer}>
        <Input
          {...register('confirmationCode', {
            required: true,
          })}
          placeholder={'Enter verification code'}
          autoComplete={'off'}
          label={'Verification code'}
          autoFocus
        />
        <PasswordInput
          {...register('newPassword', {
            required: true,
            pattern: passwordPattern,
          })}
          placeholder={'Enter new password'}
          label={'Password'}
          errorMessage={formState.errors.newPassword?.message}
        />
        <div>
          <Button
            loading={isResending}
            disabled={formState.isSubmitting}
            onClick={handleResendEmail}
            size={'small'}
            variant="ghost"
            className={subButton}
          >
            Resend email
          </Button>
        </div>
        <Button
          className={submitAuthFormButton}
          size={'large'}
          type="submit"
          loading={formState.isSubmitting}
        >
          Update password
        </Button>
      </div>
    </form>
  );
};
